import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Loader2, Store, ArrowRight, Tag, Sparkles } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const avisImg = (p) => `${API_BASE}/avis/image/${p}`;
const STORE_LABEL = { falster: "Lagersalg Falster", ringsted: "Lagersalg Ringsted" };
const STORE_PHOTO = { falster: "/stores/falster.jpg", ringsted: "/stores/ringsted.jpg" };
const STORE_ORDER = ["falster", "ringsted"];
const MONTHS_DA = ["januar","februar","marts","april","maj","juni","juli","august","september","oktober","november","december"];
const fmtRange = (from, to) => {
  if (!from || !to) return "";
  const f = new Date(`${from}T00:00:00`), t = new Date(`${to}T00:00:00`);
  if (isNaN(f.getTime()) || isNaN(t.getTime())) return "";
  if (f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear())
    return `${f.getDate()}.–${t.getDate()}. ${MONTHS_DA[t.getMonth()]} ${t.getFullYear()}`;
  return `${f.getDate()}. ${MONTHS_DA[f.getMonth()]} – ${t.getDate()}. ${MONTHS_DA[t.getMonth()]} ${t.getFullYear()}`;
};

const HOME_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
.avishome-root { font-family: 'IBM Plex Sans', system-ui, sans-serif; }
.avishome-display { font-family: 'Barlow Condensed', sans-serif; }
`;

export default function TilbudsAvisHome() {
  const [list, setList] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/avis/published`);
        if (!r.ok) { setState("error"); return; }
        setList(await r.json());
        setState("ok");
      } catch { setState("error"); }
    })();
  }, []);

  const grouped = STORE_ORDER
    .map((s) => ({ store: s, items: list.filter((a) => a.store === s) }))
    .filter((g) => g.items.length > 0);
  const other = list.filter((a) => !STORE_ORDER.includes(a.store));
  if (other.length) grouped.push({ store: "andet", items: other });

  return (
    <div className="avishome-root min-h-screen bg-[#F8F9FA] pb-24" data-testid="avis-home">
      <style>{HOME_CSS}</style>

      {/* Header */}
      <header className="bg-gradient-to-br from-red-700 via-[#C8102E] to-rose-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 relative text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full pl-2.5 pr-3.5 py-1.5 mb-5">
            <Tag className="w-4 h-4" />
            <span className="avishome-display font-bold uppercase tracking-wide text-sm">Lagersalg</span>
          </div>
          <h1 className="avishome-display text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.9] drop-shadow-sm">Tilbudsaviser</h1>
          <p className="text-red-100 text-sm sm:text-base mt-4 uppercase tracking-[0.22em] font-semibold">Se ugens tilbud fra vores butikker</p>
          <Link to="/ja-tak" className="mt-7 inline-flex items-center gap-2 bg-white text-[#C8102E] font-bold rounded-full px-6 py-3 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200" data-testid="jatak-link">
            <Sparkles className="w-4 h-4" /> Se Ja tak-tilbud
          </Link>
        </div>
        <div className="h-3 bg-yellow-300" />
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-10">
        {state === "loading" && (
          <div className="flex items-center justify-center py-24" data-testid="avis-home-loading">
            <Loader2 className="w-7 h-7 animate-spin text-[#D90429]" />
          </div>
        )}

        {state === "error" && (
          <p className="text-center text-zinc-400 py-24" data-testid="avis-home-error">Kunne ikke hente tilbudsaviserne lige nu.</p>
        )}

        {state === "ok" && list.length === 0 && (
          <div className="text-center py-24" data-testid="avis-home-empty">
            <Newspaper className="w-12 h-12 text-red-200 mx-auto mb-4" />
            <p className="avishome-display text-2xl font-extrabold uppercase tracking-tight text-zinc-800">Ingen aviser lige nu</p>
            <p className="text-zinc-500 text-sm mt-2">Kig forbi igen snart — nye tilbud er på vej.</p>
          </div>
        )}

        {state === "ok" && grouped.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-testid="avis-home-stores">
            {grouped.map((g) => (
              <section key={g.store} data-testid={`avis-home-store-${g.store}`} className="bg-white rounded-3xl border border-zinc-200 shadow-sm overflow-hidden">
                {/* Butik-banner med bygningsbillede */}
                <div className="relative aspect-[16/7] bg-gradient-to-br from-red-700 to-rose-700 overflow-hidden">
                  {STORE_PHOTO[g.store] && (
                    <img src={STORE_PHOTO[g.store]} alt={STORE_LABEL[g.store] || g.store} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Store className="w-5 h-5 text-white/90 shrink-0" />
                      <h2 className="avishome-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-white drop-shadow-md truncate">
                        {STORE_LABEL[g.store] || g.store}
                      </h2>
                    </div>
                    <span className="shrink-0 avishome-display text-xs font-bold uppercase tracking-wide bg-white/95 text-[#C8102E] rounded-full px-3 py-1 shadow-sm">
                      {g.items.length} {g.items.length === 1 ? "avis" : "aviser"}
                    </span>
                  </div>
                </div>

                <div className="p-5 grid grid-cols-1 gap-4">
                  {g.items.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/avis/${a.slug}`}
                      className="group bg-white rounded-2xl border border-zinc-200/90 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-200 overflow-hidden flex flex-col"
                      data-testid={`avis-home-card-${a.slug}`}
                    >
                      <div className="relative w-full aspect-[16/9] overflow-hidden flex items-center justify-center border-b border-zinc-100 bg-gradient-to-br from-rose-50 to-red-100">
                        {a.cover
                          ? <img src={avisImg(a.cover)} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                          : <Newspaper className="w-12 h-12 text-[#C8102E]/25" />}
                        <div className="absolute top-3 left-3 avishome-display bg-[#C8102E] text-white font-extrabold uppercase text-xs tracking-wide px-2.5 py-1 rounded-md shadow-sm">
                          Tilbudsavis
                        </div>
                      </div>
                      <div className="p-4 flex flex-col flex-grow">
                        <h3 className="avishome-display text-xl font-extrabold uppercase tracking-tight text-zinc-900 leading-tight line-clamp-2">{a.name}</h3>
                        {(a.week_no || fmtRange(a.valid_from, a.valid_to)) && (
                          <p className="text-xs font-semibold text-[#C8102E] mt-1.5" data-testid={`avis-home-validity-${a.slug}`}>
                            {a.week_no ? `Uge ${a.week_no}` : ""}{a.week_no && fmtRange(a.valid_from, a.valid_to) ? " · " : ""}{fmtRange(a.valid_from, a.valid_to) ? `gælder ${fmtRange(a.valid_from, a.valid_to)}` : ""}
                          </p>
                        )}
                        <p className="text-xs text-zinc-500 mt-1">{a.item_count} varer</p>
                        <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-[#C8102E] font-bold text-sm">
                          Se avisen <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <footer className="mt-16 border-t border-zinc-200 pt-6 text-center">
          <p className="avishome-display text-lg font-extrabold uppercase tracking-tight text-zinc-800">Lagersalg</p>
          <p className="text-xs text-zinc-500 mt-2 max-w-xl mx-auto">Priserne gælder så længe lager haves. Der tages forbehold for udsolgte varer og trykfejl.</p>
        </footer>
      </main>
    </div>
  );
}
